import { ProgressItemStage, Progress, ProgressStatus, ProgressByItem } from "../progress"
import { recordKeys } from "../../../utils/typescript"

export type ProgressSummary<S extends string> = {
  status: ProgressStatus | "disabled"
  numItems: number,
  progress: number
  activeItemsPerStage: Partial<Record<S, number>>
  errors: ProgressError<S>[]
}

export type ProgressError<S extends string> = {
  error: Error
  item: string
  stage: S
}

export function summarizeProgress<S extends string>(
  progress: Progress<S>,
  /** Relative weight for each stage for progress estimation, default is 1.0 */
  stageDurationBias?: Partial<Record<S, number>>
): ProgressSummary<S> {
  let totalWeight = 0
  let weightedProgress = 0

  const uniqueItems = new Set<string>()
  const errors: ProgressError<S>[] = []
  let hasLoading = false
  let allIdle = true
  let allDone = true

  const activeItemsPerStage = {} as Partial<Record<S, number>>

  for (const stage of recordKeys(progress.byStage)) {
    const stageItems = progress.byStage[stage] as ProgressByItem
    recordKeys(stageItems).forEach(item => uniqueItems.add(item))
    const summary = summarizeStage(stage, stageItems)

    const weight = stageDurationBias?.[stage as S] ?? 1

    const stageWeight = weight * summary.total
    totalWeight += stageWeight
    weightedProgress += summary.progress * stageWeight

    if (summary.activeCount > 0) {
      activeItemsPerStage[stage as S] = summary.activeCount
    }

    errors.push(...summary.errors)
    if (summary.hasLoading) hasLoading = true
    if (!summary.allIdle) allIdle = false
    if (!summary.allDone) allDone = false
  }

  if (uniqueItems.size === 0) {
    return {
      status: "disabled",
      numItems: 0,
      progress: 0,
      activeItemsPerStage: {},
      errors: [],
    } satisfies ProgressSummary<S>
  }

  const progressValue = totalWeight === 0 ? 0 : weightedProgress / totalWeight

  let status: ProgressSummary<S>["status"]

  if (errors.length > 0) status = "error"
  else if (hasLoading) status = "loading"
  else if (allIdle) status = "idle"
  else if (allDone) status = "done"
  else status = "loading"

  return {
    status,
    numItems: uniqueItems.size,
    progress: progressValue,
    activeItemsPerStage,
    errors
  } satisfies ProgressSummary<S>
}

function summarizeStage<S extends string>(
  stage: S,
  items: Record<string, ProgressItemStage>
) {
  let total = 0
  let completed = 0

  const errors: ProgressError<S>[] = []
  let hasLoading = false
  let allIdle = true
  let allDone = true

  let activeCount = 0

  for (const item in items) {
    const entry = items[item]
    total++

    switch (entry.status) {
      case "error":
        errors.push({
          error: entry.error,
          stage,
          item,
        })
        allIdle = false
        allDone = false
        activeCount++
        break

      case "loading":
        hasLoading = true
        allIdle = false
        allDone = false
        completed += entry.progress ?? 0
        activeCount++
        break

      case "done":
        allIdle = false
        completed += 1
        break

      case "idle":
        allDone = false
        break
    }
  }

  return {
    progress: total === 0 ? 0 : completed / total,
    total,
    errors,
    hasLoading,
    allIdle,
    allDone,
    activeCount
  } as const
}
